import {
  deleteApi,
  getApi,
  getApiById,
  getApiListWithoutId,
  postApi,
  postApiByPathParam,
  postApiPagination,
  putApi,
} from '../../Shared/Services/RestService';
import { PagedStatsResponse } from '../../Shared/Pagination/PaginationResponse';
import ApiClient from '../../Shared/Services/ApiClient';
import { ApiResponse } from '../../Shared/Services/ApiResponse';
import {
  ColumnMeta,
  ConnectionTestResult,
  DataAccessGrant,
  DataAccessRequest,
  DataAccessStats,
  DataConnection,
  DataConnectionRequest,
  DataConnectionStats,
  DataConnectionTestRequest,
  DataQueryRequest,
  DataQueryResult,
  DataSavedQuery,
  DataSavedQueryRequest,
  DataSavedQueryStats,
  ConfigurableContext,
  ConfigurableCompany,
  ConfigListFilters,
  DataServiceConfig,
  DataServiceConfigRequest,
  DataServiceConfigStats,
  GrantableUser,
  MyDataServiceAccess,
  QueryHistoryItem,
  RawSqlRequest,
  RowMutationRequest,
  TableMetaList,
  ValueSuggestionRequest,
  ValueSuggestionResult,
} from '../Types/DataServiceType';

const CONNECTIONS = '/data-service/connections';
const SAVED_QUERIES = '/data-service/saved-queries';
const ACCESS = '/data-service/access';
const DATA_SERVICE = '/data-service';
const CONFIG = '/data-service/config';

/**
 * The data-service advice returns a bare DataServiceErrorBody, not the usual
 * CommonResponse envelope, so the shared helpers cannot be trusted to unwrap a
 * rejection. This turns either shape into one consistent result.
 */
const unwrapError = (payload: any, fallback: string): string => {
  if (!payload) return fallback;
  if (typeof payload === 'string') return payload;
  if (payload.code && payload.message) return payload.message;
  if (payload.message) return payload.message;
  return fallback;
};

const fail = (payload: any, fallback: string): ApiResponse<null> => ({
  success: false,
  status: 0,
  message: unwrapError(payload, fallback),
  data: null,
  errors: payload ?? null,
});

/**
 * The data-service delete endpoints answer 204 No Content, so `deleteApi` hands
 * back an empty body rather than the usual envelope. An empty body means the
 * delete succeeded; only a real error body should be surfaced.
 */
const deleteResource = async (endpoint: string, id: string, successMessage: string) => {
  const res = await deleteApi<unknown>(endpoint, id);
  if (res && typeof res === 'object' && (res as ApiResponse<unknown>).success === false) {
    return res as ApiResponse<null>;
  }
  return { success: true, status: 200, message: successMessage, data: null, errors: null };
};

/* ------------------------------------------------------------- connections */

export interface ConnectionFilters {
  search?: string;
  engine?: string;
  environment?: string;
  status?: string;
}

export const getDataConnections = (filters: ConnectionFilters = {}, page = 0, size?: number) => {
  const params: Record<string, unknown> = { page };
  if (filters.search) params.search = filters.search;
  if (filters.engine) params.engine = filters.engine;
  if (filters.environment) params.environment = filters.environment;
  if (filters.status) params.status = filters.status;
  if (size !== undefined) params.size = size;
  return getApi<PagedStatsResponse<DataConnection, DataConnectionStats>>(CONNECTIONS, params);
};

export const getDataConnection = (id: string) => getApiById<DataConnection>(CONNECTIONS, id);

export const createDataConnection = (payload: DataConnectionRequest) =>
  postApi<DataConnection>(CONNECTIONS, payload);

export const updateDataConnection = (id: string, payload: DataConnectionRequest) =>
  putApi<DataConnection>(CONNECTIONS, id, payload);

export const deleteDataConnection = (id: string) =>
  deleteResource(CONNECTIONS, id, 'Connection deleted.');

export const testConnectionDraft = (payload: DataConnectionTestRequest) =>
  postApi<ConnectionTestResult>(`${CONNECTIONS}/test`, payload);

export const testStoredConnection = (id: string) =>
  postApiByPathParam<ConnectionTestResult>(`${CONNECTIONS}/${id}/test`);

/* ------------------------------------------------------------------ schema */

/** GET /data-service/connections/{id}/tables */
export const getConnectionTables = (id: string, includeSystemSchemas = false) =>
  getApi<TableMetaList>(`${DATA_SERVICE}/connections/${id}/tables`, { includeSystemSchemas });

/** GET /data-service/connections/{id}/tables/{table}/columns */
export const getTableColumns = (connectionId: string, table: string) =>
  getApi<ColumnMeta[]>(
    `${DATA_SERVICE}/connections/${connectionId}/tables/${encodeURIComponent(table)}/columns`
  );

export interface DataRecordRequest {
  tableName: string;
  key: Record<string, string>;
}

/**
 * Reads a single row by its primary key, for the record drill-down.
 *
 * <p>Not a builder query on purpose: the builder refuses to filter a column it
 * has classified as binary, which is how MySQL stores these uuid keys, so
 * `WHERE id = ?` is not expressible there. The server also only accepts a key
 * that matches the table's real primary key.</p>
 */
export const fetchDataRecord = (connectionId: string, payload: DataRecordRequest) =>
  postApi<DataQueryResult>(`${DATA_SERVICE}/connections/${connectionId}/record`, payload);

/* ------------------------------------------------------------------- query */

export const runDataQuery = (payload: DataQueryRequest) =>
  postApi<DataQueryResult>(`${DATA_SERVICE}/query`, payload);

export const suggestValues = (payload: ValueSuggestionRequest) =>
  postApi<ValueSuggestionResult>(`${DATA_SERVICE}/suggestions/values`, payload);

export const runConsole = (payload: RawSqlRequest) =>
  postApi<DataQueryResult>(`${DATA_SERVICE}/console`, payload);

export const mutateRow = async (payload: RowMutationRequest) => {
  try {
    const res = await ApiClient.post(`${DATA_SERVICE}/rows`, payload);
    return res.data as ApiResponse<{ affectedRows: number; operation: string }>;
  } catch (err: any) {
    return fail(err.response?.data, 'The change could not be applied.');
  }
};

export const getQueryHistory = (connectionId?: string, page = 0, size?: number) => {
  const params: Record<string, unknown> = { page };
  if (connectionId) params.connectionId = connectionId;
  if (size !== undefined) params.size = size;
  return getApi<PagedStatsResponse<QueryHistoryItem, number>>(`${DATA_SERVICE}/history`, params);
};

/* ------------------------------------------------------------ saved queries */

export interface SavedQueryFilters {
  search?: string;
  connectionId?: string;
  favoritesOnly?: boolean;
}

export const getSavedQueries = (filters: SavedQueryFilters = {}, page = 0, size?: number) => {
  const params: Record<string, unknown> = { page };
  if (filters.search) params.search = filters.search;
  if (filters.connectionId) params.connectionId = filters.connectionId;
  if (filters.favoritesOnly) params.favoritesOnly = true;
  if (size !== undefined) params.size = size;
  return getApi<PagedStatsResponse<DataSavedQuery, DataSavedQueryStats>>(SAVED_QUERIES, params);
};

export const getSavedQuery = (id: string) => getApiById<DataSavedQuery>(SAVED_QUERIES, id);

export const getFavoriteSavedQueries = () => getApiListWithoutId<DataSavedQuery[]>(`${SAVED_QUERIES}/favorites`);

export const createSavedQuery = (payload: DataSavedQueryRequest) =>
  postApi<DataSavedQuery>(SAVED_QUERIES, payload);

export const updateSavedQuery = (id: string, payload: DataSavedQueryRequest) =>
  putApi<DataSavedQuery>(SAVED_QUERIES, id, payload);

export const deleteSavedQuery = (id: string) =>
  deleteResource(SAVED_QUERIES, id, 'Saved query deleted.');

export const runSavedQuery = (id: string, limit?: number) => {
  const params: Record<string, unknown> = {};
  if (limit !== undefined) params.limit = limit;
  return postApiPagination<DataQueryResult>(`${SAVED_QUERIES}/${id}/run`, undefined, params);
};

/* ------------------------------------------------------------------ access */

export const getAccessGrants = (filter: { userId?: string; connectionId?: string }) => {
  const params: Record<string, unknown> = {};
  if (filter.userId) params.userId = filter.userId;
  if (filter.connectionId) params.connectionId = filter.connectionId;
  return getApi<DataAccessGrant[]>(ACCESS, params);
};

export const getAccessStats = () => getApi<DataAccessStats>(`${ACCESS}/stats`);

export const grantDataAccess = (payload: DataAccessRequest) => postApi<DataAccessGrant>(ACCESS, payload);

export const revokeDataAccess = (id: string) =>
  deleteResource(ACCESS, id, 'Access revoked.');

/** Users a grant can be handed to. Served by the data service so it can search. */
export const getGrantableUsers = (search = '', page = 0, size = 50) => {
  const params: Record<string, unknown> = { page, size };
  const term = search.trim();
  if (term) params.search = term;
  return getApi<PagedStatsResponse<GrantableUser, number>>(`${ACCESS}/grantable-users`, params);
};

/* ------------------------------------------------- per-owner table configuration */

export const getCompanyConfigs = (companyId: string) =>
  getApi<DataServiceConfig[]>(`${CONFIG}/companies/${companyId}`);

export const getUserConfigs = (userId: string) => getApi<DataServiceConfig[]>(`${CONFIG}/users/${userId}`);

export const getConnectionConfigs = (connectionId: string) =>
  getApi<DataServiceConfig[]>(`${CONFIG}/connections/${connectionId}`);

export const getConfigStats = () => getApi<DataServiceConfigStats>(`${CONFIG}/stats`);

/** Who the caller is, and who they may configure. Server-decided, not role-guessed. */
export const getConfigurableContext = () => getApi<ConfigurableContext>(`${CONFIG}/context`);

/**
 * Companies, for the admin picker on a company-wide grant. Only fetched when
 * the context says the caller may name a company.
 */
export const getConfigurableCompanies = () => getApiListWithoutId<ConfigurableCompany[]>('/companies');

/**
 * Every configuration the caller may see. This is the management listing, so the
 * screen no longer depends on which subject happens to be selected in the form
 * - which is what previously let a freshly saved row look like it had vanished.
 */
export const getAllConfigs = (filters: ConfigListFilters = {}) => {
  const params: Record<string, unknown> = {};
  if (filters.search?.trim()) params.search = filters.search.trim();
  if (filters.scope) params.scope = filters.scope;
  if (filters.source) params.source = filters.source;
  if (filters.companyId) params.companyId = filters.companyId;
  if (filters.userId) params.userId = filters.userId;
  if (filters.connectionId) params.connectionId = filters.connectionId;
  if (filters.includeInactive) params.includeInactive = true;
  return getApi<DataServiceConfig[]>(CONFIG, params);
};

/** Edits a row in place, keeping its id. */
export const updateDataServiceConfig = (id: string, payload: DataServiceConfigRequest) =>
  putApi<DataServiceConfig>(CONFIG, id, payload);

/** Drives the Data Service entry in the recruiter and candidate navigation. */
export const getMyDataServiceAccess = () => getApi<MyDataServiceAccess>(`${CONFIG}/my-access`);

/**
 * Creates the configuration, replacing any live row for the same owner and
 * connection - so this doubles as the edit call.
 */
export const saveDataServiceConfig = (payload: DataServiceConfigRequest) =>
  postApi<DataServiceConfig>(CONFIG, payload);

export const revokeDataServiceConfig = (id: string) =>
  deleteResource(CONFIG, id, 'Configuration revoked.');
